import { Link } from 'react-router-dom'
import { ChevronRight, Home as HomeIcon, LayoutDashboard } from 'lucide-react'

/**
 * Generic breadcrumb: "{Home} > [...trail] > {current}".
 *
 * Two-level shape unchanged for callers that pass only `current`
 * (the original API). Optional `trail` adds clickable intermediate
 * crumbs between Home and Current — used by deep pages like the
 * per-company dashboard (`Dashboard > Companies > {Name}`) where the
 * "parent list" is a real navigable page, not just an aria
 * decoration.
 *
 * Each trail entry is `{ label, to }`. Pass at least one to render
 * intermediate crumbs; omit it for the original two-level layout.
 */
export function Breadcrumb({
  current,
  homePath = '/dashboard',
  homeLabel = 'Dashboard',
  trail = [],
}) {
  const Icon = homeLabel === 'Home' ? HomeIcon : LayoutDashboard
  const isRoot = !current

  return (
    <nav
      aria-label="Breadcrumb"
      className="mb-4 flex min-w-0 items-center gap-2 text-sm text-[color:var(--color-ink-muted)]"
    >
      {isRoot ? (
        <span className="flex items-center gap-1 text-[color:var(--color-ink-strong)] font-medium">
          <Icon size={14} />
          <span>{homeLabel}</span>
        </span>
      ) : (
        <>
          <Link
            to={homePath}
            className="flex shrink-0 items-center gap-1 hover:text-[color:var(--color-ink-strong)] cursor-pointer"
          >
            <Icon size={14} />
            <span>{homeLabel}</span>
          </Link>
          {trail.map((crumb) => (
            <span key={crumb.to} className="flex shrink-0 items-center gap-2">
              <ChevronRight size={14} aria-hidden="true" />
              <Link
                to={crumb.to}
                className="hover:text-[color:var(--color-ink-strong)] cursor-pointer"
              >
                {crumb.label}
              </Link>
            </span>
          ))}
          <ChevronRight size={14} aria-hidden="true" className="shrink-0" />
          {/* The current crumb (e.g. a long goal name) truncates instead of
           *  pushing the page wide; the full text stays available on hover. */}
          <span
            className="min-w-0 truncate text-[color:var(--color-ink-strong)] font-medium"
            title={typeof current === 'string' ? current : undefined}
          >
            {current}
          </span>
        </>
      )}
    </nav>
  )
}
